import * as Sentry from '@sentry/browser';
import React, { Component, type ReactNode } from 'react';
import ErrorPopup from '/@/components/error-popup';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error,
        };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log error to Sentry
        if ((window as any).SENTRY_PUBLIC_DSN) {
            Sentry.withScope((scope) => {
                scope.setExtras({
                    componentStack: errorInfo.componentStack,
                });
                Sentry.captureException(error);
            });
        }

        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
        });
    };

    override render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const errorDetails =
                process.env.NODE_ENV === 'development' && this.state.error ? (
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {this.state.error.toString()}
                        {this.state.error.stack}
                    </pre>
                ) : undefined;

            return <ErrorPopup fullPage showResetButton onReset={this.handleReset} errorDetails={errorDetails} />;
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
